"use client";

import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

import { css, sva } from "@/../styled-system/css";

const scrollarea = sva({
  slots: ["root", "viewport", "scrollbar", "thumb"],
  base: {
    root: { position: "relative", overflow: "hidden" },
    viewport: { h: "full", w: "full", rounded: "inherit" },
    scrollbar: {
      display: "flex",
      touchAction: "none",
      userSelect: "none",
      transition: "colors",
      "&[data-orientation=vertical]": {
        h: "full",
        w: "10px",
        borderLeftWidth: "1px",
        borderLeftColor: "transparent",
        p: "1px",
      },
      "&[data-orientation=horizontal]": {
        w: "100%",
        flexDir: "column",
        borderTopWidth: "1px",
        borderTopColor: "transparent",
        backgroundColor: "neutral.100",
        rounded: "full",
      },
    },
    thumb: {
      position: "relative",
      flex: "1",

      "&[data-orientation=vertical]": {
        rounded: "unset",
        backgroundColor: "neutral.300",
      },
      "&[data-orientation=horizontal]": {
        rounded: "full",
        backgroundColor: "brandBlue",
      },
    },
  },
});

const { root, viewport, scrollbar, thumb } = scrollarea.raw();

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    css?: object;
  }
>(({ css: cssProp = {}, children, ...props }, ref) => {
  const className = css(root, cssProp);

  return (
    <ScrollAreaPrimitive.Root ref={ref} className={className} {...props}>
      <ScrollAreaPrimitive.Viewport className={css(viewport)}>
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > & { css?: object }
>(({ orientation = "vertical", css: cssProp = {}, ...props }, ref) => {
  const className = css(scrollbar, cssProp);

  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={className}
      data-orientation={orientation}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-orientation={orientation}
        className={css(thumb)}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
});
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
